import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { fetchArticle, fetchArticles, fetchProduct } from "../lib/api";
import Breadcrumbs from "../components/Breadcrumbs";
import ShareButtons from "../components/ShareButtons";
import { articlePath, setCanonical, useNoindex } from "../lib/seo";
import AuthorBlock from "../components/AuthorBlock";
import TableOfContents, { slugify } from "../components/TableOfContents";
import AffiliateCTA from "../components/AffiliateCTA";
import RelatedArticles from "../components/RelatedArticles";

export default function ReviewDetail() {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [product, setProduct] = useState(null);
  const [related, setRelated] = useState([]);
  const [notFound, setNotFound] = useState(false);
  useNoindex(notFound || !!article?.noindex);

  useEffect(() => {
    setArticle(null);
    setNotFound(false);
    fetchArticle(slug)
      .then((a) => {
        setArticle(a);
        document.title = `${a.title} | SaaSRadarHub`;
        setCanonical(articlePath(a));
        if (a.related_product_slug) {
          fetchProduct(a.related_product_slug).then(setProduct).catch(() => {});
        }
        fetchArticles({ category: a.category, limit: 4 }).then((list) => {
          setRelated(list.filter((x) => x.slug !== a.slug).slice(0, 3));
        });
      })
      .catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) {
    return (
      <div className="max-w-3xl mx-auto px-5 py-24 text-center">
        <h1 className="font-playfair text-3xl mb-3">Review not found</h1>
        <Link to="/reviews" className="text-indigo-800 underline">Back to reviews</Link>
      </div>
    );
  }
  if (!article) return <div className="max-w-3xl mx-auto px-5 py-24 text-neutral-500">Loading…</div>;

  const sections = article.sections || [];

  return (
    <article className="max-w-7xl mx-auto px-5 lg:px-8 py-14" data-testid="review-detail">
      <Breadcrumbs
        items={[
          { label: "Reviews", to: "/reviews" },
          { label: article.title },
        ]}
      />
      <header className="max-w-3xl mb-10">
        <p className="eyebrow mb-4">{article.category} · Review</p>
        <h1 className="font-playfair text-4xl sm:text-5xl lg:text-6xl text-neutral-900 tracking-tight leading-[1.05] mb-5">
          {article.title}
        </h1>
        <p className="text-lg text-neutral-600 leading-relaxed">{article.excerpt}</p>
        <div className="mt-6"><ShareButtons kind="reviews" slug={article.slug} title={article.title} /></div>
      </header>

      {article.image_url && (
        <figure className="mb-10 relative aspect-[16/9] sm:aspect-[21/9] rounded-xl overflow-hidden bg-neutral-100" data-testid="article-cover">
          <img src={article.image_url} alt={article.title} className="absolute inset-0 w-full h-full object-cover" />
        </figure>
      )}

      <AuthorBlock
        author={article.author}
        title={`${article.author_title} · ${article.reading_time} min read`}
        date={article.publication_date}
        updated={article.updated_date}
      />

      <div className="mt-12 grid grid-cols-1 lg:grid-cols-12 gap-12">
        <aside className="lg:col-span-3">
          <TableOfContents sections={sections} />
        </aside>

        <div className="lg:col-span-9 max-w-3xl editorial-prose">
          {sections.map((s, i) => (
            <section key={i} id={slugify(s.heading)} className="scroll-mt-24">
              <h2>{s.heading}</h2>
              <p>{s.body}</p>
              {product && i === 3 && (
                <AffiliateCTA product={product.name} url={product.affiliate_url || product.website_url} />
              )}
            </section>
          ))}

          <div className="mt-12 pt-8 border-t border-neutral-200">
            <p className="eyebrow mb-3">Affiliate disclosure</p>
            <p className="text-sm text-neutral-600 leading-relaxed">
              Some links on SaaSRadarHub may be affiliate links. We may receive a
              commission if you purchase or sign up through them, at no
              additional cost to you.{" "}
              <Link to="/affiliate-disclosure" className="text-indigo-800 underline">
                Read our full disclosure
              </Link>
              .
            </p>
          </div>
        </div>
      </div>

      <RelatedArticles articles={related} title="More in this category" />
    </article>
  );
}
